import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadsCountryStatsEndpoint } from "@/config/endpoints";
import { transientRetry } from "@/lib/queryClient";

/**
 * Per-country lead counts for the header "Pick country" picker. Fires the
 * filter-aware `/core/leads/country-stats` endpoint with the SAME filter
 * params the leads list uses, so every country count "swings" with branch /
 * stage / status / search / date / other CFs — exactly like the branch picker.
 *
 * The server strips the Country CF facet itself (`cf_<countryFieldId>`) so
 * selecting one country doesn't zero out the others, then counts each option
 * with the EXACT list `where` for that country — so a row's number always
 * equals what clicking it shows (a multi-country lead counts in every
 * matching bucket; Σ counts can exceed `total`). `total` powers the
 * "All country" row (distinct in-scope leads).
 *
 * GENERIC over any dropdown field: the header's KEY-FIELD pickers ("Pick
 * team") call it with the key field's id as `countryFieldId`, and the server
 * strips and counts that field exactly the same way.
 *
 * Disabled until `countryFieldId` (and `enabled`) are truthy, so
 * non-study-abroad tenants never fire it. `counts` is `undefined` until the
 * first response arrives, so callers can hide the numbers while loading.
 */
export function useCountryLeadStats(
  countryFieldId: number | null | undefined,
  filters: Record<string, any> = {},
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled !== false && !!countryFieldId;

  const query = useQuery<{
    total: number;
    counts: Record<string, number>;
    noCountry: number;
  }>({
    queryKey: ["country-stats", countryFieldId, filters],
    queryFn: async () => {
      const res = await axiosInstance.get(leadsCountryStatsEndpoint, {
        params: { ...filters, countryFieldId },
      });
      return {
        total: Number(res.data?.total ?? 0),
        counts: (res.data?.counts ?? {}) as Record<string, number>,
        // Leads with no country anywhere (the list's `cf=none` sentinel), so
        // clicking "No country" reconciles too. Only under the Verified anchor
        // do the buckets still partition the total (rep-deal attribution).
        noCountry: Number(res.data?.noCountry ?? 0),
      };
    },
    enabled,
    staleTime: 60 * 1000,
    ...transientRetry,
  });

  return {
    counts: query.data?.counts,
    total: query.data?.total ?? 0,
    noCountry: query.data?.noCountry ?? 0,
    isLoading: query.isLoading,
  };
}
